<template>
  <transition name="slide-fade">
    <div v-if="show" class="insight-panel-overlay" @click.self="$emit('close')">
      <div class="insight-panel shadow-lg">
        <div class="insight-header d-flex justify-content-between align-items-center">
          <h4 class="m-0 text-white font-weight-bold">Insight Brief</h4>
          <button @click="$emit('close')" class="close-btn text-white">
            <b-icon icon="x" font-scale="1.5"></b-icon>
          </button>
        </div>

        <div class="insight-body p-4">
          <div v-if="loading" class="text-center my-5">
            <b-spinner variant="teal" label="Spinning"></b-spinner>
            <p class="mt-2 text-muted">Analyzing live data...</p>
          </div>

          <div v-else-if="errorMessage" class="text-center my-5">
            <b-icon icon="exclamation-triangle" variant="warning" font-scale="2"></b-icon>
            <p class="mt-3 text-muted">{{ errorMessage }}</p>
          </div>

          <div v-else>
            <div class="insight-section mb-4">
              <h5 class="section-title">Trend Summary</h5>
              <p class="section-content">
                {{ generatedInsight.trendSummary }}
              </p>
            </div>

            <div class="insight-section mb-4">
              <h5 class="section-title">Key Observations</h5>
              <ul class="section-content">
                <li v-for="(obs, index) in generatedInsight.observations" :key="index">{{ obs }}</li>
              </ul>
            </div>

            <div class="insight-section mb-4">
              <h5 class="section-title">Possible Implications</h5>
              <p class="section-content">
                {{ generatedInsight.implication }}
              </p>
            </div>

            <div v-if="generatedInsight.metadata" class="insight-meta text-muted small">
              <span>Data points analyzed: {{ generatedInsight.metadata.dataPointCount }}</span>
              <span v-if="generatedInsight.metadata.currentPeriod">
                · Latest period: {{ generatedInsight.metadata.currentPeriod }}
              </span>
            </div>
          </div>
        </div>

        <div class="insight-footer p-3 border-top d-flex flex-column gap-2">
          <b-button
            @click="copyInsight"
            variant="outline-success"
            class="action-btn w-100 mb-2"
            :disabled="loading || !!errorMessage"
          >
            <b-icon icon="clipboard" class="mr-1"></b-icon> Copy Insight
          </b-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import apiServices from '@/modules/data-layer/services/ApiServices';

export default {
  name: 'InsightPanel',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({
        indicator: 'N/A',
        location: 'N/A',
        period: 'N/A',
        datasource: 'N/A',
      }),
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      generatedInsight: {
        trendSummary: '',
        observations: [],
        implication: '',
        metadata: null,
      },
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.generateInsight();
      }
    },
  },
  methods: {
    async generateInsight() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await apiServices.getInsightBrief(this.filters);
        const brief = response.data;

        if (brief.error || !brief.dataAvailable) {
          this.errorMessage = brief.message || 'Unable to generate insight from the selected data.';
          this.generatedInsight = {
            trendSummary: '',
            observations: [],
            implication: '',
            metadata: null,
          };
          return;
        }

        this.generatedInsight = {
          trendSummary: brief.trendSummary,
          observations: brief.observations || [],
          implication: brief.implication,
          metadata: brief.metadata || null,
        };
      } catch (error) {
        const apiMessage = error.response && error.response.data && error.response.data.message;
        this.errorMessage = apiMessage || error.message || 'Unable to generate insight from the selected data.';
        this.generatedInsight = {
          trendSummary: '',
          observations: [],
          implication: '',
          metadata: null,
        };
      } finally {
        this.loading = false;
      }
    },
    copyInsight() {
      const summaryText = `
Insight Brief
Indicator: ${this.filters.indicator}
Location: ${this.filters.location}
Period: ${this.filters.period}
Source: ${this.filters.datasource}

Trend Summary:
${this.generatedInsight.trendSummary}

Key Observations:
${this.generatedInsight.observations.map((obs) => `- ${obs}`).join('\n')}

Possible Implications:
${this.generatedInsight.implication}
      `.trim();

      navigator.clipboard.writeText(summaryText).then(() => {
        this.$swal({
          title: 'Success!',
          text: 'Insight copied to clipboard!',
          icon: 'success',
          timer: 3000,
        });
      });
    },
  },
};
</script>

<style scoped>
.insight-panel-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.insight-panel {
  width: 450px;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.insight-body {
  flex-grow: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.insight-header {
  background: #007d53;
  padding: 1.5rem;
  flex-shrink: 0;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #007d53;
  margin-bottom: 0.75rem;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.25rem;
}

.section-content {
  color: #495057;
  line-height: 1.6;
}

.insight-meta {
  border-top: 1px solid #e9ecef;
  padding-top: 0.75rem;
}

.insight-footer {
  background: #f8f9fa;
  margin-top: auto;
}

.action-btn {
  font-weight: 600;
  border-radius: 8px;
}

.gap-2 {
  gap: 0.5rem;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(450px);
  opacity: 0;
}

@media (max-width: 576px) {
  .insight-panel {
    width: 100%;
  }
}
</style>
